import {
  useCurrentDataStore,
  useSetCurrentRecord,
} from 'components/DataStoreReference/DataStoreReference';
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AnyColumn, Platform } from 'componentregistry';
import {
  getScrollbarSize,
  makeColumnWidths,
  updateVisibleCells,
} from './GridUtils';
import { isEditable } from 'components/CanvasGrid/cells/utils';
import { BoundType, FocusCell, VisibleCells } from './types';
import { useObservable } from '@legendapp/state/react';
import { ObservableObject } from '@legendapp/state';
import { DataRecord } from '@cloudio-saas/datasource-types';
import { usePlatform } from 'platform/PlatformHooks';
import { useScrollValue } from './GridScroll';
import {
  useEvent,
  useStateXAtomSetter,
  useStateXAtomValue,
} from '@cloudio-saas/statex';
import {
  dataGridSelectedPath,
  recordIdBeforeRefresh,
} from 'layout/Sidebar/atoms';
import { ColumnGroup } from '../useColumns';

export interface Props {
  bodyHeight: number;
  columnWidthFn: (columnIndex: number) => number;
  columns: AnyColumn[];
  expandLastColumn?: boolean;
  autoFitColumns?: boolean;
  footerRows: number;
  headerRows: number;
  height: number;
  rowHeight: number;
  scrollToColumn: number;
  scrollToRow: number;
  totalColumns: number;
  totalRows: number;
  width: number;
  allowDownArrowRecordCreation?: boolean;
  gridItemId: string;
  pageId: string;
  appUid: string;
  groupColumns?: AnyColumn[];
  h1GroupColumns?: AnyColumn[];
  h2GroupColumns?: AnyColumn[];
  h3GroupColumns?: AnyColumn[];
  h1LeftFixedColumnGroups?: ColumnGroup[][];
  h1RightFixedColumnGroups?: ColumnGroup[][];
  h1BodyColumnGroups?: ColumnGroup[][];
  h2LeftFixedColumnGroups?: ColumnGroup[][];
  h2RightFixedColumnGroups?: ColumnGroup[][];
  h2BodyColumnGroups?: ColumnGroup[][];
  h3LeftFixedColumnGroups?: ColumnGroup[][];
  h3RightFixedColumnGroups?: ColumnGroup[][];
  h3BodyColumnGroups?: ColumnGroup[][];
}

export interface BoundProps {
  bodyHeight: number;
  bodyWidth: number;
  columnBuffer: number;
  columnWidths: number[];
  fixedLeftColumns: number;
  fixedRightColumns: number;
  footerRows: number;
  headerRows: number;
  rowBuffer: number;
  rowHeight: number;
  scrollHeight: number;
  scrollWidth: number;
  totalColumns: number;
  totalRows: number;
  gridBoundsSignal: ObservableObject<BoundType>;
}

interface onKeyDownFnProps {
  footerRows: number;
  headerRows: number;
  pageSize: number;
  totalColumns: number;
  totalRows: number;
}

function onKeyDownFn(
  e: React.KeyboardEvent<HTMLDivElement>,
  props: onKeyDownFnProps,
  focusSignal: ObservableObject<FocusCell>,
  allowDownArrowRecordCreation: boolean | undefined,
  columns: AnyColumn[],
  appUid: string,
  pageId: string,
  gridItemId: string,
  currentStore: any,
  platform: Platform,
  setdataGridSelectedPath: (
    value: React.SetStateAction<{
      dragFromCell: { rowIndex: number; columnIndex: number };
      dragToCell: { rowIndex: number; columnIndex: number };
      dragging: boolean;
    }>,
  ) => {
    dragFromCell: { rowIndex: number; columnIndex: number };
    dragToCell: { rowIndex: number; columnIndex: number };
    dragging: boolean;
  },
  dragFromCell: { rowIndex: number; columnIndex: number },
  dragToCell: { rowIndex: number; columnIndex: number },
): FocusCell {
  const { footerRows, headerRows, pageSize, totalColumns, totalRows } = props;
  const { focusRow, focusColumn } = focusSignal.peek();
  let { editing } = focusSignal.peek();
  let nextRow = focusRow;
  let nextColumn = focusColumn;
  let { key } = e;
  if (editing) {
    if (key === 'ArrowLeft' || key === 'ArrowRight') {
      return { focusRow, focusColumn, editing };
    }
    if (key === 'Tab') {
      key = e.shiftKey ? 'ArrowLeft' : 'ArrowRight';
    }
  }

  // eslint-disable-next-line default-case
  switch (key) {
    case 'ArrowDown':
      nextRow++;
      if (e.shiftKey) {
        dragToCell = { rowIndex: nextRow, columnIndex: nextColumn };
      }
      break;
    case 'ArrowUp':
      nextRow--;
      if (e.shiftKey) {
        dragToCell = { rowIndex: nextRow, columnIndex: nextColumn };
      }
      break;
    case 'ArrowLeft':
      nextColumn--;
      if (nextColumn < 0) {
        if (nextRow > headerRows) {
          nextColumn = totalColumns - 1;
          nextRow--;
        } else {
          nextColumn = 0;
        }
      }
      if (e.shiftKey) {
        dragFromCell = { rowIndex: nextRow, columnIndex: focusColumn };
        dragToCell = { rowIndex: nextRow, columnIndex: nextColumn };
      }
      break;
    case 'ArrowRight':
      nextColumn = focusColumn;
      let column = columns[nextColumn];
      do {
        nextColumn += 1;
        column = columns[nextColumn];
      } while (
        nextColumn < totalColumns &&
        !isEditable({
          column,
          platform,
          appUid,
          pageId,
          rowIndex: focusRow - headerRows,
          store: currentStore,
          gridItemId,
          dataGrid: true,
        })
      );
      if (nextColumn >= totalColumns) {
        if (nextRow < totalRows - 1) {
          nextColumn = 0;
          nextRow++;
        } else {
          nextColumn = totalColumns - 1;
        }
      }
      if (e.shiftKey) {
        dragToCell = { rowIndex: nextRow, columnIndex: nextColumn };
      }
      break;
    case 'Enter':
      dragFromCell = { rowIndex: nextRow, columnIndex: nextColumn };
      dragToCell = { rowIndex: nextRow, columnIndex: nextColumn };
      e.preventDefault();
      e.stopPropagation();
      editing = !editing;
      break;
    case 'Escape':
      e.preventDefault();
      e.stopPropagation();
      editing = false;
      break;
    case 'Home':
      nextColumn = 0;
      if (e.ctrlKey) {
        nextRow = headerRows;
      }
      break;
    case 'End':
      nextColumn = totalColumns - 1;
      if (e.ctrlKey) {
        nextRow = totalRows - 1 - footerRows;
      }
      break;
    case 'PageDown':
      nextRow += pageSize;
      nextRow = Math.min(totalRows - 1 - footerRows, nextRow);
      break;
    case 'PageUp':
      nextRow -= pageSize;
      nextRow = Math.max(headerRows, nextRow);
      break;
  }
  nextRow = Math.max(
    headerRows,
    allowDownArrowRecordCreation ? nextRow : Math.min(nextRow, totalRows - 1),
  );
  if (!(e.shiftKey || e.metaKey || e.ctrlKey)) {
    dragFromCell = { rowIndex: nextRow, columnIndex: nextColumn };
    dragToCell = { rowIndex: nextRow, columnIndex: nextColumn };
  }
  setdataGridSelectedPath({
    dragFromCell,
    dragToCell,
    dragging: false,
  });
  if (nextRow !== focusRow || nextColumn !== focusColumn) {
    e.preventDefault();
    e.stopPropagation();
  }
  return {
    focusRow: nextRow,
    focusColumn: nextColumn,
    editing,
    dragFromCell,
    dragToCell,
  };
}

export function useGridMetadata(props: Props) {
  const {
    bodyHeight,
    columnWidthFn,
    columns,
    expandLastColumn,
    autoFitColumns,
    footerRows,
    headerRows,
    height,
    rowHeight,
    scrollToColumn,
    scrollToRow,
    totalColumns,
    totalRows,
    width,
    allowDownArrowRecordCreation,
    gridItemId,
    pageId,
    appUid,
    h1GroupColumns = [],
    h2GroupColumns = [],
    h3GroupColumns = [],
    h1LeftFixedColumnGroups,
    h1RightFixedColumnGroups,
    h1BodyColumnGroups,
    h2LeftFixedColumnGroups,
    h2RightFixedColumnGroups,
    h2BodyColumnGroups,
    h3LeftFixedColumnGroups,
    h3RightFixedColumnGroups,
    h3BodyColumnGroups,
  } = props;
  const currentStore = useCurrentDataStore<DataRecord>();
  // useWhyDidYouUpdate('useGridMetadata', { ...props });

  const h1ColumnGroups = useMemo(
    () => [
      ...(h1LeftFixedColumnGroups ?? []),
      ...(h1RightFixedColumnGroups ?? []),
      ...(h1BodyColumnGroups ?? []),
    ],
    [h1BodyColumnGroups, h1LeftFixedColumnGroups, h1RightFixedColumnGroups],
  );

  const h2ColumnGroups = useMemo(
    () => [
      ...(h2LeftFixedColumnGroups ?? []),
      ...(h2RightFixedColumnGroups ?? []),
      ...(h2BodyColumnGroups ?? []),
    ],
    [h2BodyColumnGroups, h2LeftFixedColumnGroups, h2RightFixedColumnGroups],
  );

  const h3ColumnGroups = useMemo(
    () => [
      ...(h3LeftFixedColumnGroups ?? []),
      ...(h3RightFixedColumnGroups ?? []),
      ...(h3BodyColumnGroups ?? []),
    ],
    [h3BodyColumnGroups, h3LeftFixedColumnGroups, h3RightFixedColumnGroups],
  );

  const focusSignal = useObservable<FocusCell>(() => ({
    focusColumn: scrollToColumn,
    focusRow: scrollToRow + headerRows,
    editing: false,
  }));
  const platform = usePlatform();
  const mv = useScrollValue();
  const boundSignal = useObservable<BoundType>(() => ({
    pos: { left: 0, top: 0 },
    bound: {
      row: { end: 0, start: 0 },
      column: {
        end: -1,
        start: 0,
      },
      actualRow: { end: 0, start: 0 },
    },
  }));

  const setdataGridSelectedPath = useStateXAtomSetter(dataGridSelectedPath);
  const { dragFromCell, dragToCell } = useStateXAtomValue(dataGridSelectedPath);

  const actualBodyHeight = (totalRows - headerRows - footerRows) * rowHeight;
  const h = Math.max(
    actualBodyHeight,
    height - (headerRows + footerRows) * rowHeight,
  );

  const _scrollbarSize = getScrollbarSize();
  const vertialScrollbarSize = actualBodyHeight < h ? 0 : _scrollbarSize;

  const {
    columnWidths: _columnWidths,
    columnLefts: _columnLefts,
    innerWidth: _innerWidth,
  } = useMemo(() => {
    return makeColumnWidths(
      width,
      totalColumns,
      columnWidthFn,
      columns,
      vertialScrollbarSize,
      expandLastColumn,
      autoFitColumns,
    );
  }, [
    columnWidthFn,
    columns,
    expandLastColumn,
    autoFitColumns,
    totalColumns,
    vertialScrollbarSize,
    width,
  ]);

  const {
    columnWidths: _h1GroupColumnWidths,
    columnLefts: _h1GroupColumnLefts,
  } = useMemo(() => {
    return makeColumnWidths(
      width,
      h1GroupColumns.length,
      columnWidthFn,
      h1GroupColumns,
      vertialScrollbarSize,
      expandLastColumn,
      autoFitColumns,
      h1ColumnGroups,
      columns,
    );
  }, [
    columnWidthFn,
    columns,
    expandLastColumn,
    autoFitColumns,
    h1ColumnGroups,
    h1GroupColumns,
    vertialScrollbarSize,
    width,
  ]);

  const {
    columnWidths: _h2GroupColumnWidths,
    columnLefts: _h2GroupColumnLefts,
  } = useMemo(() => {
    return makeColumnWidths(
      width,
      h2GroupColumns.length,
      columnWidthFn,
      h2GroupColumns,
      vertialScrollbarSize,
      expandLastColumn,
      autoFitColumns,
      h2ColumnGroups,
      columns,
    );
  }, [
    columnWidthFn,
    columns,
    expandLastColumn,
    autoFitColumns,
    h2ColumnGroups,
    h2GroupColumns,
    vertialScrollbarSize,
    width,
  ]);

  const {
    columnWidths: _h3GroupColumnWidths,
    columnLefts: _h3GroupColumnLefts,
  } = useMemo(() => {
    return makeColumnWidths(
      width,
      h3GroupColumns.length,
      columnWidthFn,
      h3GroupColumns,
      vertialScrollbarSize,
      expandLastColumn,
      autoFitColumns,
      h3ColumnGroups,
      columns,
    );
  }, [
    columnWidthFn,
    columns,
    expandLastColumn,
    autoFitColumns,
    h3ColumnGroups,
    h3GroupColumns,
    vertialScrollbarSize,
    width,
  ]);

  const pageSize = Math.ceil(bodyHeight / rowHeight);

  const cacheRef = useRef({
    footerRows,
    headerRows,
    pageSize,
    totalColumns,
    totalRows,
  });
  const setCurrentRecord = useSetCurrentRecord();
  const recordsOrder = currentStore?.recordsOrder();

  useEffect(() => {
    return focusSignal.focusRow.onChange((focusRow) => {
      setCurrentRecord(focusRow - headerRows);
    });
  }, [focusSignal, headerRows, setCurrentRecord]);

  const currentRecordId = currentStore?.getCurrentRecordId() || '';
  useEffect(() => {
    const rowIndex = recordsOrder?.indexOf(currentRecordId);
    // for Virtual Lists, Header Rows are 0
    if (rowIndex && headerRows !== 0)
      focusSignal.set((v) => ({
        ...v,
        focusRow: rowIndex + headerRows,
      }));
  }, [currentRecordId, focusSignal, headerRows, recordsOrder]);

  useLayoutEffect(() => {
    cacheRef.current = {
      footerRows,
      headerRows,
      pageSize,
      totalColumns,
      totalRows,
    };
  }, [footerRows, headerRows, pageSize, totalColumns, totalRows]);

  useEffect(() => {
    focusSignal.set((v) => ({
      ...v,
      focusColumn: scrollToColumn,
      focusRow: scrollToRow + headerRows,
    }));
  }, [scrollToColumn, scrollToRow, headerRows, focusSignal]);

  useEffect(() => {
    if (!currentStore || currentStore?.ignorePKDuplicate) return;
    const recordIdBeforeRefreshValue =
      currentStore &&
      platform.getter(recordIdBeforeRefresh, {
        params: { dsAlias: currentStore.alias },
      });
    currentStore &&
      platform.setter(recordIdBeforeRefresh, undefined, {
        params: { dsAlias: currentStore.alias },
      });
    if (recordIdBeforeRefreshValue) {
      const index =
        recordIdBeforeRefreshValue &&
        recordsOrder?.indexOf(recordIdBeforeRefreshValue);
      const addedBodySize =
        (index ? index + headerRows : headerRows + 1) * rowHeight -
        height +
        rowHeight +
        rowHeight;
      currentStore?.setCurrentRecordId(
        recordIdBeforeRefreshValue,
        undefined,
        true,
      );
      mv.wheeling = true;
      if (height + addedBodySize > height) {
        boundSignal.pos.top.set(addedBodySize);
        mv.set({ y: -addedBodySize });
      }
    }
  }, [
    recordsOrder,
    boundSignal.pos.top,
    currentStore,
    headerRows,
    height,
    mv,
    platform,
    rowHeight,
  ]);

  const onKeyDown = useEvent((e: React.KeyboardEvent<HTMLDivElement>) => {
    const { focusRow, editing } = focusSignal.peek();
    if (
      editing &&
      e.key === 'ArrowDown' &&
      focusRow + 1 + footerRows === totalRows &&
      allowDownArrowRecordCreation
    ) {
      currentStore &&
        currentStore.createNew({
          partialRecord: {},
          addOnTop: false,
          rs: 'N',
        });
    }
    const resp = onKeyDownFn(
      e,
      cacheRef.current,
      focusSignal,
      allowDownArrowRecordCreation,
      columns,
      appUid,
      pageId,
      gridItemId,
      currentStore,
      platform,
      setdataGridSelectedPath,
      dragFromCell,
      dragToCell,
    );
    const focusCell = focusSignal.peek();
    if (
      resp.focusRow !== focusCell.focusRow ||
      resp.focusColumn !== focusCell.focusColumn ||
      resp.editing !== focusCell.editing
    ) {
      focusSignal.set(resp);
      if (
        editing &&
        e.key === 'ArrowDown' &&
        focusRow + 1 + footerRows === totalRows &&
        allowDownArrowRecordCreation
      ) {
        const addedBodySize =
          totalRows * rowHeight - height + _scrollbarSize + rowHeight;
        boundSignal.pos.top.set(addedBodySize);
        mv.wheeling = true;
        mv.set({ y: -addedBodySize });
      }
    }
  });

  const [{ columnWidths, columnLefts, innerWidth }, setColumnState] = useState({
    columnLefts: _columnLefts,
    columnWidths: _columnWidths,
    innerWidth: _innerWidth,
  });

  const [{ h1GroupColumnLefts, h1GroupColumnWidths }, setH1GroupColumnState] =
    useState({
      h1GroupColumnLefts: _h1GroupColumnLefts,
      h1GroupColumnWidths: _h1GroupColumnWidths,
    });

  const [{ h2GroupColumnLefts, h2GroupColumnWidths }, setH2GroupColumnState] =
    useState({
      h2GroupColumnLefts: _h2GroupColumnLefts,
      h2GroupColumnWidths: _h2GroupColumnWidths,
    });

  const [{ h3GroupColumnLefts, h3GroupColumnWidths }, setH3GroupColumnState] =
    useState({
      h3GroupColumnLefts: _h3GroupColumnLefts,
      h3GroupColumnWidths: _h3GroupColumnWidths,
    });

  const joinStr = _columnWidths.join(',');
  const joinH1GroupWidthStr = _h1GroupColumnWidths.join(',');
  const joinH2GroupWidthStr = _h2GroupColumnWidths.join(',');
  const joinH3GroupWidthStr = _h3GroupColumnWidths.join(',');

  useLayoutEffect(() => {
    const {
      columnLefts: __columnLefts,
      columnWidths: __columnWidths,
      innerWidth: __innerWidth,
    } = makeColumnWidths(
      width,
      totalColumns,
      columnWidthFn,
      columns,
      vertialScrollbarSize,
      expandLastColumn,
      autoFitColumns,
    );

    if (joinStr !== columnWidths?.join(',')) {
      setColumnState({
        columnLefts: __columnLefts,
        columnWidths: __columnWidths,
        innerWidth: __innerWidth,
      });
    }
  }, [
    columnWidthFn,
    totalColumns,
    joinStr,
    columnWidths,
    columns,
    width,
    vertialScrollbarSize,
    expandLastColumn,
    autoFitColumns,
  ]);

  useLayoutEffect(() => {
    const {
      columnLefts: __h1GroupColumnLefts,
      columnWidths: __h1GroupColumnWidths,
    } = makeColumnWidths(
      width,
      h1GroupColumns.length,
      columnWidthFn,
      h1GroupColumns,
      vertialScrollbarSize,
      expandLastColumn,
      autoFitColumns,
      h1ColumnGroups,
      columns,
    );
    if (joinH1GroupWidthStr !== h1GroupColumnWidths?.join(',')) {
      setH1GroupColumnState({
        h1GroupColumnLefts: __h1GroupColumnLefts,
        h1GroupColumnWidths: __h1GroupColumnWidths,
      });
    }
  }, [
    columnWidthFn,
    columns,
    expandLastColumn,
    autoFitColumns,
    h1ColumnGroups,
    h1GroupColumnWidths,
    h1GroupColumns,
    joinH1GroupWidthStr,
    vertialScrollbarSize,
    width,
  ]);

  useLayoutEffect(() => {
    const {
      columnLefts: __h2GroupColumnLefts,
      columnWidths: __h2GroupColumnWidths,
    } = makeColumnWidths(
      width,
      h2GroupColumns.length,
      columnWidthFn,
      h2GroupColumns,
      vertialScrollbarSize,
      expandLastColumn,
      autoFitColumns,
      h2ColumnGroups,
      columns,
    );
    if (joinH2GroupWidthStr !== h2GroupColumnWidths?.join(',')) {
      setH2GroupColumnState({
        h2GroupColumnLefts: __h2GroupColumnLefts,
        h2GroupColumnWidths: __h2GroupColumnWidths,
      });
    }
  }, [
    columnWidthFn,
    columns,
    expandLastColumn,
    autoFitColumns,
    h2ColumnGroups,
    h2GroupColumnWidths,
    h2GroupColumns,
    joinH2GroupWidthStr,
    vertialScrollbarSize,
    width,
  ]);

  useLayoutEffect(() => {
    const {
      columnLefts: __h3GroupColumnLefts,
      columnWidths: __h3GroupColumnWidths,
    } = makeColumnWidths(
      width,
      h3GroupColumns.length,
      columnWidthFn,
      h3GroupColumns,
      vertialScrollbarSize,
      expandLastColumn,
      autoFitColumns,
      h3ColumnGroups,
      columns,
    );
    if (joinH3GroupWidthStr !== h3GroupColumnWidths?.join(',')) {
      setH3GroupColumnState({
        h3GroupColumnLefts: __h3GroupColumnLefts,
        h3GroupColumnWidths: __h3GroupColumnWidths,
      });
    }
  }, [
    columnWidthFn,
    columns,
    expandLastColumn,
    autoFitColumns,
    h3ColumnGroups,
    h3GroupColumnWidths,
    h3GroupColumns,
    joinH3GroupWidthStr,
    vertialScrollbarSize,
    width,
  ]);

  return {
    boundSignal,
    columnLefts: columnLefts ?? [],
    columnWidths: columnWidths ?? [],
    innerWidth,
    onKeyDown,
    focusSignal,
    h1GroupColumnLefts,
    h1GroupColumnWidths,
    h2GroupColumnLefts,
    h2GroupColumnWidths,
    h3GroupColumnLefts,
    h3GroupColumnWidths,
  };
}

export function useGridBoundsMetaData(props: BoundProps) {
  const {
    bodyHeight,
    bodyWidth,
    columnBuffer,
    columnWidths,
    fixedLeftColumns,
    fixedRightColumns,
    footerRows,
    headerRows,
    rowBuffer,
    rowHeight,
    scrollHeight,
    scrollWidth,
    totalColumns,
    totalRows,
    gridBoundsSignal,
  } = props;
  const previousVisibleCells = useRef<VisibleCells>({
    startRow: 0,
    endRow: 0,
    startColumn: 0,
    endColumn: 0,
    totalRows: 0,
    actualEndRow: 0,
    actualStartRow: 0,
  });
  useEffect(() => {
    function run(left: number, top: number) {
      const {
        startRow,
        endRow,
        startColumn,
        endColumn,
        actualEndRow,
        actualStartRow,
      } = updateVisibleCells({
        columnWidths,
        fixedLeftColumns,
        fixedRightColumns,
        footerRows,
        headerRows,
        position: {
          bodyHeight,
          scrollHeight,
          scrollWidth,
          bodyWidth,
          left,
          top,
        },
        rowHeight,
        totalColumns,
        totalRows,
        rowBuffer,
        columnBuffer,
        previousVisibleCells: previousVisibleCells.current,
      });

      const { column, row, actualRow } = gridBoundsSignal.bound.peek();
      if (column.end !== endColumn || column.start !== startColumn) {
        gridBoundsSignal.bound.column.set({
          start: startColumn,
          end: endColumn,
        });
      }
      if (row.end !== endRow || row.start !== startRow) {
        gridBoundsSignal.bound.row.set({
          start: startRow,
          end: endRow,
        });
      }
      if (
        actualRow.end !== actualEndRow ||
        actualRow.start !== actualStartRow
      ) {
        gridBoundsSignal.bound.actualRow.set({
          start: actualStartRow,
          end: actualEndRow,
        });
      }
    }
    const { left, top } = gridBoundsSignal.pos.get();
    run(left, top);
    return gridBoundsSignal.pos.onChange(({ left: _left, top: _top }) => {
      run(_left, _top);
    });
  }, [
    bodyHeight,
    bodyWidth,
    columnBuffer,
    columnWidths,
    fixedLeftColumns,
    fixedRightColumns,
    footerRows,
    gridBoundsSignal.bound,
    gridBoundsSignal.pos,
    headerRows,
    rowBuffer,
    rowHeight,
    scrollHeight,
    scrollWidth,
    totalColumns,
    totalRows,
  ]);
}
